import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useEndpointLogs, type ConnectionStatus } from '@/hooks/useEndpointLogs'
import { deleteEndpointLogs, type RequestLog } from '@/api/logs'

// ── helpers ────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function statusColor(code: number): string {
  if (code >= 500) return 'text-red-600 font-semibold'
  if (code >= 400) return 'text-amber-600 font-semibold'
  if (code >= 300) return 'text-blue-600'
  return 'text-green-600'
}

const METHOD_COLORS: Record<string, string> = {
  GET:     'bg-blue-100 text-blue-700 border-blue-200',
  POST:    'bg-green-100 text-green-700 border-green-200',
  PUT:     'bg-orange-100 text-orange-700 border-orange-200',
  PATCH:   'bg-purple-100 text-purple-700 border-purple-200',
  DELETE:  'bg-red-100 text-red-700 border-red-200',
  HEAD:    'bg-gray-100 text-gray-600 border-gray-200',
  OPTIONS: 'bg-gray-100 text-gray-600 border-gray-200',
}

function MethodBadge({ method }: { method: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-mono font-semibold',
        METHOD_COLORS[method] ?? 'bg-gray-100 text-gray-600 border-gray-200',
      )}
    >
      {method}
    </span>
  )
}

function ConnectionIndicator({ status }: { status: ConnectionStatus }) {
  const { t } = useTranslation()
  const label = status === 'connected' ? 'LIVE' : status === 'connecting' ? t('logViewer.connecting') : t('logViewer.disconnected')
  const dotClass =
    status === 'connected'
      ? 'bg-green-500 animate-pulse'
      : status === 'connecting'
      ? 'bg-yellow-400 animate-pulse'
      : 'bg-gray-400'

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span className={cn('h-2 w-2 rounded-full', dotClass)} />
      {label}
    </span>
  )
}

// ── Log detail content ─────────────────────────────────────────────────────

function LogDetailContent({ log }: { log: RequestLog }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-4 text-sm">
      {/* Meta */}
      <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
        <div><span className="font-medium text-foreground">{t('logViewer.detailTime')}: </span>{new Date(log.created_at).toLocaleString('ja-JP')}</div>
        <div><span className="font-medium text-foreground">{t('logViewer.detailLatency')}: </span>{log.latency_ms} ms</div>
        <div><span className="font-medium text-foreground">{t('logViewer.detailIp')}: </span>{log.remote_ip ?? '—'}</div>
        <div><span className="font-medium text-foreground">{t('logViewer.detailUa')}: </span><span className="truncate">{log.user_agent ?? '—'}</span></div>
      </div>

      {typeof log.query === 'object' && log.query !== null && Object.keys(log.query).length > 0 && (
        <Section label={t('logViewer.queryParams')} value={prettyJson(log.query)} />
      )}
      <Section label={t('logViewer.requestHeaders')} value={prettyJson(log.request_headers)} />
      {log.request_body && (
        <Section label={t('logViewer.requestBody')} value={tryPrettyJson(log.request_body)} />
      )}
      <Section label={t('logViewer.responseHeaders')} value={prettyJson(log.response_headers)} />
      {log.response_body && (
        <Section label={t('logViewer.responseBody')} value={tryPrettyJson(log.response_body)} />
      )}
    </div>
  )
}

// ── Detail dialog ──────────────────────────────────────────────────────────

function prettyJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

function LogDetailDialog({
  log,
  onClose,
}: {
  log: RequestLog
  onClose: () => void
}) {
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-mono text-sm font-semibold pr-8">
            <MethodBadge method={log.method} />
            <span className="truncate min-w-0">{log.path}</span>
            <span className={cn('ml-auto shrink-0 text-base', statusColor(log.response_status))}>
              {log.response_status}
            </span>
          </DialogTitle>
        </DialogHeader>

        <LogDetailContent log={log} />
      </DialogContent>
    </Dialog>
  )
}

function tryPrettyJson(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2)
  } catch {
    return s
  }
}

function Section({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
      <pre className="rounded-md bg-muted px-3 py-2 text-xs font-mono whitespace-pre-wrap break-all overflow-auto max-h-48">
        {value}
      </pre>
    </div>
  )
}

// ── LogViewer ──────────────────────────────────────────────────────────────

interface LogViewerProps {
  projectId: string
  endpointId: string
}

export function LogViewer({ projectId, endpointId }: LogViewerProps) {
  const { logs, isLoading, connectionStatus, loadMore, clearLogs, hasMore } = useEndpointLogs(endpointId, projectId)
  const [selectedLog, setSelectedLog] = useState<RequestLog | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [showClearDialog, setShowClearDialog] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const { t } = useTranslation()

  const handleLoadMore = async () => {
    setIsLoadingMore(true)
    try {
      await loadMore()
    } finally {
      setIsLoadingMore(false)
    }
  }

  const handleClearAll = async () => {
    setIsClearing(true)
    try {
      await deleteEndpointLogs(endpointId)
      clearLogs()
      setShowClearDialog(false)
      toast.success(t('logViewer.clearAllSuccess'))
    } catch {
      toast.error(t('logViewer.clearAllError'))
    } finally {
      setIsClearing(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t('logViewer.title')}</h2>
        <div className="flex items-center gap-3">
          <ConnectionIndicator status={connectionStatus} />
          {logs.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowClearDialog(true)}
              className="gap-1.5 text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t('logViewer.clearAll')}
            </Button>
          )}
        </div>
      </div>

      <Dialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('logViewer.clearAllConfirmTitle')}</DialogTitle>
            <DialogDescription>{t('logViewer.clearAllConfirmDesc')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearDialog(false)} disabled={isClearing}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={() => void handleClearAll()} disabled={isClearing}>
              {isClearing ? t('common.loading') : t('logViewer.clearAll')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">{t('logViewer.colTime')}</TableHead>
              <TableHead className="w-24">{t('logViewer.colMethod')}</TableHead>
              <TableHead>{t('logViewer.colPath')}</TableHead>
              <TableHead className="w-16 text-center">{t('logViewer.colStatus')}</TableHead>
              <TableHead className="w-20 text-right">{t('logViewer.colLatency')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="h-20 text-center text-muted-foreground text-sm">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="h-20 text-center text-muted-foreground text-sm">
                  {t('logViewer.empty')}
                </TableCell>
              </TableRow>
            )}
            {logs.map((log) => (
              <TableRow
                key={log.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => setSelectedLog(log)}
              >
                <TableCell className="font-mono text-xs text-muted-foreground">
                  {formatTime(log.created_at)}
                </TableCell>
                <TableCell>
                  <MethodBadge method={log.method} />
                </TableCell>
                <TableCell className="font-mono text-xs max-w-xs truncate">
                  {log.path}
                  {typeof log.query === 'object' && log.query !== null && Object.keys(log.query).length > 0 && (
                    <span className="text-muted-foreground">
                      ?{new URLSearchParams(log.query as Record<string, string>).toString()}
                    </span>
                  )}
                </TableCell>
                <TableCell className={cn('text-center text-sm', statusColor(log.response_status))}>
                  {log.response_status}
                </TableCell>
                <TableCell className="text-right text-xs text-muted-foreground">
                  {log.latency_ms} ms
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {hasMore && !isLoading && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" onClick={() => void handleLoadMore()} disabled={isLoadingMore}>
            {isLoadingMore ? t('logViewer.loadingMore') : t('logViewer.loadMore')}
          </Button>
        </div>
      )}

      {selectedLog && (
        <LogDetailDialog log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  )
}
